import styled from 'styled-components'
import {
	backgroundOrange,
	darkOrange,
	green,
	success,
	danger,
	dangerTint,
	black,
	textMuted,
	white,
	warmTint,
	border1,
} from '@/theme/colors'
import { radii, media, motion } from '@/theme/tokens'
import { fontSizes, text, leading } from '@/theme/typography'
import { Button } from '@components/Button'

export const SectionRoot = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`

export const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding-bottom: 14px;
	border-bottom: 1px solid ${warmTint};
	margin-bottom: 14px;
	flex-shrink: 0;
	flex-wrap: wrap;
`

export const SectionTitle = styled.h3`
	${text.title}
	color: ${darkOrange};
	margin: 0;
`

export const SectionBody = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding-right: 4px;
	display: flex;
	flex-direction: column;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}
`

export const MemberSectionList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`

export const InlineSummaryCard = styled.div<{
	$tone: 'settled' | 'owe' | 'owed' | 'mixed'
}>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	background: ${({ $tone }) => {
		if ($tone === 'settled') return `${success}1f`
		if ($tone === 'owe') return dangerTint
		if ($tone === 'owed') return `${success}1f`
		return white
	}};
	border: 1px solid
		${({ $tone }) => {
			if ($tone === 'settled') return `${success}52`
			if ($tone === 'owe') return `${danger}40`
			if ($tone === 'owed') return `${success}52`
			return warmTint
		}};
	border-radius: ${radii.inner};
	padding: 10px 14px;
	margin-bottom: 14px;
	flex-shrink: 0;
`

export const InlineSummaryLabel = styled.span`
	${text.eyebrow}
	color: ${textMuted};
`

export const InlineSummaryValue = styled.span<{
	$tone: 'settled' | 'owe' | 'owed' | 'mixed'
}>`
	font-size: ${fontSizes.body};
	font-weight: 700;
	color: ${({ $tone }) => {
		if ($tone === 'settled') return green
		if ($tone === 'owe') return danger
		if ($tone === 'owed') return green
		return black
	}};
	text-align: right;
`

export const InlineSettledNote = styled.span`
	font-size: ${fontSizes.caption};
	font-weight: 700;
	color: ${green};
	display: block;
	margin-bottom: 14px;
	text-align: center;
	flex-shrink: 0;
`

export const HeaderActionButton = styled(Button).attrs({ size: 'sm' as const })`
	white-space: nowrap;
`

export const MemberSectionWrapper = styled.div`
	background: ${white};
	border-radius: ${radii.inner};
	border: 1px solid ${warmTint};
	overflow: hidden;
`

export const MemberHeaderRow = styled.button<{ $clickable: boolean }>`
	width: 100%;
	background: ${white};
	border: none;
	padding: 14px 18px;
	display: flex;
	align-items: center;
	gap: 12px;
	text-align: left;
	cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
	transition: background ${motion.fast};
	flex-wrap: wrap;

	&:hover {
		background: ${({ $clickable }) => ($clickable ? backgroundOrange : 'white')};
	}

	${media.phone} {
		padding: 12px 14px;
		gap: 8px;
	}
`


export const MemberHeaderMain = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
	min-width: 0;
`

export const MemberHeadline = styled.div`
	font-size: ${fontSizes.base};
	color: ${black};
	line-height: ${leading.body};
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`

export const MemberHeadlineLine = styled.span`
	display: block;
	overflow-wrap: anywhere;
	word-break: break-word;
`

export const MemberHeadlineName = styled.span`
	font-weight: 700;
	color: ${black};
	display: inline-block;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
`

export const MemberHeadlineAmount = styled.span<{ $tone: 'owe' | 'owed' }>`
	font-weight: 700;
	color: ${({ $tone }) => ($tone === 'owe' ? danger : green)};
`

export const ExpandChevron = styled.span<{ $expanded: boolean }>`
	color: ${darkOrange};
	font-size: ${fontSizes.body};
	flex-shrink: 0;
	transition: transform ${motion.fast};
	transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`

export const BreakdownContainer = styled.div`
	background: ${backgroundOrange};
	padding: 12px 18px 16px 18px;
	border-top: 1px solid ${border1};
	display: flex;
	flex-direction: column;
	gap: 10px;
`

export const BreakdownRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	background: ${white};
	border-radius: ${radii.inner};
	padding: 12px 14px;
	border: 1px solid ${warmTint};
	min-width: 0;
`

export const BreakdownSentence = styled.div`
	font-size: ${fontSizes.small};
	color: ${black};
	line-height: ${leading.body};
`

export const BreakdownSentenceAmount = styled.span<{ $tone: 'owe' | 'owed' }>`
	font-weight: 700;
	color: ${({ $tone }) => ($tone === 'owe' ? danger : green)};
`

export const RecordPayUpButton = styled(Button).attrs({ size: 'sm' as const })`
	align-self: flex-start;
`

